import type { ReactNode } from 'react';

import { Button, Link, Typography } from '@mui/material';

import { AuthHeroLayout } from './AuthHeroLayout';

type AuthHeroMessageProps = {
  title: string;
  subtitle?: ReactNode;
  action?: { label: string; onClick: () => void };
  /** Extra content under the subtitle, e.g. the company switcher. */
  children?: ReactNode;
  /** Shows "Need help? Reach out to support@truv.com" at the bottom of the gradient. */
  withSupport?: boolean;
};

const SupportLine = () => (
  <Typography className="text-[14px] leading-[20.3px] text-white/70">
    Need help? Reach out to{' '}
    <Link
      href="mailto:support@truv.com"
      underline="always"
      className="text-white/70 hover:text-white"
      sx={{ textDecorationColor: 'currentColor' }}
    >
      support@truv.com
    </Link>
  </Typography>
);

/** A status screen (pending, declined, errors) centred on the auth gradient. */
export const AuthHeroMessage = ({ title, subtitle, action, children, withSupport = false }: AuthHeroMessageProps) => (
  <AuthHeroLayout variant="message" footer={withSupport ? <SupportLine /> : null}>
    <div className="flex flex-col items-center gap-10">
      <div className="flex flex-col items-center gap-6">
        <Typography variant="h1" color="inherit" fontSize={{ xs: 36, md: 62 }} fontWeight={500} lineHeight={1.05}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            fontSize={{ xs: 16, md: 20 }}
            lineHeight={1.35}
            letterSpacing="0.01em"
            fontWeight={500}
            className="text-white/70"
          >
            {subtitle}
          </Typography>
        )}
      </div>
      {children}
      {action && (
        <Button
          variant="contained"
          onClick={action.onClick}
          className="min-w-[160px] bg-white text-primary-main shadow-none hover:bg-white/90 hover:shadow-none"
        >
          {action.label}
        </Button>
      )}
    </div>
  </AuthHeroLayout>
);
